<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { ST } from '@/constants/ST'

const emit = defineEmits(['show-notification'])

const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const templates = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const sortField = ref('')
const sortOrder = ref('')
const listas = reactive({})

const filtros = reactive({
  codigoModulo: '',
  busca: ''
})

const modal = reactive({
  visivel: false,
  salvando: false,
  slotsImagem: [],
  item: {
    idRelatorioTemplate: 0,
    nome: '',
    descricao: '',
    codigoModulo: '',
    caminhoComponente: '',
    versao: '1.0.0',
    lstStatus: ST.Ativo,
    isPublico: true,
    idEmpresa: null,
    configuracaoJson: null,
    slotsImagemJson: null,
    thumbnailUrl: null
  }
})

// ====================================================================
// CONFIGURAÇÃO DE LAYOUT DO RELATÓRIO (ConfiguracaoJson)
// ====================================================================

const PRESETS_MARGENS = {
  muitoEstreita: { top: 15, right: 15, bottom: 15, left: 15 },
  estreita: { top: 25, right: 25, bottom: 25, left: 25 },
  normal: { top: 40, right: 60, bottom: 40, left: 60 }
}

const OPCOES_PAPEL = [
  { value: 'A4', label: 'A4 (210×297mm)' },
  { value: 'Letter', label: 'Letter (216×279mm)' },
  { value: 'Legal', label: 'Legal (216×356mm)' },
  { value: 'A3', label: 'A3 (297×420mm)' }
]

const configLayout = reactive({
  pageSize: 'A4',
  pageOrientation: 'portrait',
  margins: { ...PRESETS_MARGENS.normal }
})

const margemAtiva = computed(() => {
  const m = configLayout.margins
  for (const [key, preset] of Object.entries(PRESETS_MARGENS)) {
    if (m.top === preset.top && m.right === preset.right && m.bottom === preset.bottom && m.left === preset.left) {
      return key
    }
  }
  return 'personalizado'
})

function aplicarPreDefinicaoMargens (nome) {
  const preset = PRESETS_MARGENS[nome]
  if (preset) {
    configLayout.margins.top = preset.top
    configLayout.margins.right = preset.right
    configLayout.margins.bottom = preset.bottom
    configLayout.margins.left = preset.left
  }
}

function parseConfiguracaoJson (json) {
  if (!json) {
    configLayout.pageSize = 'A4'
    configLayout.pageOrientation = 'portrait'
    configLayout.margins = { ...PRESETS_MARGENS.normal }
    return
  }
  try {
    const parsed = JSON.parse(json)
    configLayout.pageSize = parsed.pageSize || 'A4'
    configLayout.pageOrientation = parsed.pageOrientation || 'portrait'
    const arr = parsed.pageMargins || [40, 60, 40, 60]
    configLayout.margins = {
      top: arr[0] ?? 40,
      right: arr[1] ?? 60,
      bottom: arr[2] ?? 40,
      left: arr[3] ?? 60
    }
  } catch {
    // fallback silencioso para defaults
  }
}

function serializeConfiguracaoJson () {
  const m = configLayout.margins
  return JSON.stringify({
    pageSize: configLayout.pageSize,
    pageOrientation: configLayout.pageOrientation,
    pageMargins: [m.top, m.right, m.bottom, m.left]
  })
}

const colunas = computed(() => [
  { field: 'nome', label: 'Nome', sortable: true, class: 'fw-medium' },
  { field: 'codigoModulo', label: 'Módulo', width: 150, sortable: true },
  { field: 'versao', label: 'Versão', width: 100, sortable: true },
  { field: 'isPublico', label: 'Público', width: 100, align: 'center', slotName: 'isPublico' },
  { field: 'idEmpresa', label: 'Empresa', width: 130, slotName: 'idEmpresa' },
  { field: 'lstStatus', label: 'Status', width: 110, align: 'center', slotName: 'lstStatus' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

function getTextoLista (lista, codigo) {
  if (!lista || !codigo) return codigo || '-'
  const item = lista.find(i => String(i.idListaSistemaItem) === String(codigo) || String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

async function carregarListas () {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['RelatorioTemplate'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null
  try {
    const params = injetarParametros(pageNumber)
    if (filtros.codigoModulo) params.append('codigoModulo', filtros.codigoModulo)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/RelatorioTemplates?${params.toString()}`)
    templates.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar templates:', error)
    erro.value = 'Não foi possível carregar os templates.'
    templates.value = []
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.codigoModulo = ''
  filtros.busca = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const abrirCadastro = () => {
  modal.item = {
    idRelatorioTemplate: 0,
    nome: '',
    descricao: '',
    codigoModulo: '',
    caminhoComponente: '',
    versao: '1.0.0',
    lstStatus: ST.Ativo,
    isPublico: true,
    idEmpresa: null,
    configuracaoJson: null,
    slotsImagemJson: null,
    thumbnailUrl: null
  }
  modal.slotsImagem = []
  parseConfiguracaoJson(null)
  modal.visivel = true
}

const editarTemplate = (tmpl) => {
  modal.item = {
    idRelatorioTemplate: tmpl.idRelatorioTemplate,
    nome: tmpl.nome || '',
    descricao: tmpl.descricao || '',
    codigoModulo: tmpl.codigoModulo || '',
    caminhoComponente: tmpl.caminhoComponente || '',
    versao: tmpl.versao || '1.0.0',
    lstStatus: tmpl.lstStatus || ST.Ativo,
    isPublico: tmpl.isPublico !== undefined ? tmpl.isPublico : true,
    idEmpresa: tmpl.idEmpresa || null,
    configuracaoJson: tmpl.configuracaoJson || null,
    slotsImagemJson: tmpl.slotsImagemJson || null,
    thumbnailUrl: tmpl.thumbnailUrl || null
  }
  // Parse slots de imagem do JSON
  try {
    modal.slotsImagem = tmpl.slotsImagemJson ? JSON.parse(tmpl.slotsImagemJson) : []
  } catch (e) {
    modal.slotsImagem = []
  }
  // Parse configuração de layout do JSON
  parseConfiguracaoJson(tmpl.configuracaoJson)
  modal.visivel = true
}

const adicionarSlotImagem = () => {
  modal.slotsImagem.push({ chave: '', label: '', tipoImagem: '', obrigatorio: false, maxWidth: null, maxHeight: null })
}

const removerSlotImagem = (idx) => {
  modal.slotsImagem.splice(idx, 1)
}

const salvarTemplate = async () => {
  modal.salvando = true
  try {
    // Serializar configuração de layout para JSON
    modal.item.configuracaoJson = serializeConfiguracaoJson()

    // Serializar slots de imagem para JSON
    modal.item.slotsImagemJson = modal.slotsImagem.length > 0 ? JSON.stringify(modal.slotsImagem) : null

    if (modal.item.idRelatorioTemplate) {
      await api.put(`/RelatorioTemplates/${modal.item.idRelatorioTemplate}`, modal.item)
      emit('show-notification', { message: 'Template atualizado com sucesso!', type: 'success' })
    } else {
      await api.post('/RelatorioTemplates', modal.item)
      emit('show-notification', { message: 'Template criado com sucesso!', type: 'success' })
    }
    modal.visivel = false
    buscarDados(paginacao.value.CurrentPage || 1)
  } catch (error) {
    const data = error.response?.data
    let msg = data?.message
    if (!msg && data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg || 'Erro ao salvar template.', type: 'error' })
  } finally {
    modal.salvando = false
  }
}

onMounted(() => {
  carregarListas()
  buscarDados(1)
})
</script>

<template>
  <PaginaAdministrar
    titulo="Templates de Relatório"
    subtitulo="Listagem e gerenciamento de registos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="abrirCadastro"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="true"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-modulo" class="form-label">Módulo</label>
          <input type="text" id="filtro-modulo" class="form-control" v-model="filtros.codigoModulo" placeholder="Ex: PURCHASE_ORDER" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label for="filtro-busca" class="form-label">Nome / Descrição</label>
          <input type="text" id="filtro-busca" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="templates"
      :loading="loading"
      empty-message="Nenhum template encontrado."
      row-key="idRelatorioTemplate"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      @row-dblclick="editarTemplate($event.row)"
    >
      <template #isPublico="{ row }">
        <i :class="['bi', row.isPublico ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-secondary']"></i>
      </template>

      <template #idEmpresa="{ row }">
        <span v-if="row.idEmpresa" class="badge badge-ghost-info">Exclusivo #{{ row.idEmpresa }}</span>
        <span v-else class="text-muted small">—</span>
      </template>

      <template #lstStatus="{ row }">
        <span :class="['badge', row.lstStatus === ST.Ativo ? 'badge-ghost-success' : 'badge-ghost-secondary']">
          {{ getTextoLista(listas.LstStatus, row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper">
          <button class="btn-icon text-primary" title="Editar" @click="editarTemplate(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <!-- Modal de Cadastro/Edição (ModalFormularioV2) -->
  <ModalFormularioV2
    v-model="modal.visivel"
    :titulo="modal.item.idRelatorioTemplate ? 'Editar Template de Relatório' : 'Novo Template de Relatório'"
    :loading="modal.salvando"
    tamanho="xl"
    @confirmar="salvarTemplate"
  >
    <div class="row g-3">
      <div class="col-md-8">
        <CampoInput
          id="nomeTemplate"
          label="Nome do Template"
          v-model="modal.item.nome"
          required
        />
      </div>
      <div class="col-md-4">
        <CampoInput
          id="versaoTemplate"
          label="Versão"
          v-model="modal.item.versao"
          placeholder="1.0.0"
        />
      </div>

      <div class="col-12">
        <CampoInput
          id="descricaoTemplate"
          label="Descrição"
          type="textarea"
          v-model="modal.item.descricao"
        />
      </div>

      <div class="col-md-6">
        <CampoInput
          id="codigoModulo"
          label="Código do Módulo"
          v-model="modal.item.codigoModulo"
          placeholder="Ex: PURCHASE_ORDER"
          required
          descricao-informativa="Deve corresponder ao campo Modulo da tabela RegraAcesso"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="caminhoComponente"
          label="Caminho do Componente"
          v-model="modal.item.caminhoComponente"
          placeholder="relatorios/templates/purchase-order/PurchaseOrderRelatorio"
          required
          descricao-informativa="Caminho para import dinâmico do template .vue"
        />
      </div>

      <div class="col-md-4">
        <CampoSelect
          id="lstStatusTemplate"
          label="Status"
          v-model="modal.item.lstStatus"
          :options="listas.LstStatus || []"
          option-label="textoLista"
          option-value="idListaSistemaItem"
          required
        />
      </div>
      <div class="col-md-4">
        <div class="form-check form-switch mt-4 pt-2">
          <input class="form-check-input" type="checkbox" id="isPublicoTemplate" v-model="modal.item.isPublico">
          <label class="form-check-label" for="isPublicoTemplate">
            <span class="fw-medium">Template Público?</span>
            <br><small class="text-muted">Disponível para todas as empresas</small>
          </label>
        </div>
      </div>
      <div class="col-md-4" v-if="!modal.item.isPublico">
        <CampoInput
          id="idEmpresaTemplate"
          label="Empresa Exclusiva (ID)"
          type="number"
          v-model.number="modal.item.idEmpresa"
          descricao-informativa="Preencha se for exclusivo de uma empresa"
        />
      </div>

      <!-- ================================================================ -->
      <!-- CONFIGURAÇÃO DE LAYOUT DO RELATÓRIO (ConfiguracaoJson)           -->
      <!-- ================================================================ -->
      <div class="col-12 mt-4">
        <h6 class="border-bottom pb-2 mb-3">
          <i class="bi bi-layout-three-columns me-1"></i> Configuração de Layout do Relatório
        </h6>
        <p class="small text-muted mb-3">
          Definições de tamanho de papel, orientação e margens.
        </p>

        <div class="border rounded p-3 bg-body-tertiary">
          <div class="row g-4">
            <!-- Orientação -->
            <div class="col-md-6">
              <label class="form-label fw-medium mb-2">
                <i class="bi bi-phone-landscape me-1"></i> Orientação
              </label>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="card-orientacao"
                  :class="{ ativo: configLayout.pageOrientation === 'portrait' }"
                  @click="configLayout.pageOrientation = 'portrait'"
                >
                  <div class="icone-pagina icone-retrato">
                    <div class="pagina-miniatura"></div>
                  </div>
                  <span class="small fw-medium">Retrato</span>
                </button>
                <button
                  type="button"
                  class="card-orientacao"
                  :class="{ ativo: configLayout.pageOrientation === 'landscape' }"
                  @click="configLayout.pageOrientation = 'landscape'"
                >
                  <div class="icone-pagina icone-paisagem">
                    <div class="pagina-miniatura"></div>
                  </div>
                  <span class="small fw-medium">Paisagem</span>
                </button>
              </div>
            </div>

            <!-- Tamanho do Papel -->
            <div class="col-md-6">
              <CampoSelect
                id="pageSize"
                label="Tamanho do Papel"
                v-model="configLayout.pageSize"
                :options="OPCOES_PAPEL"
                option-label="label"
                option-value="value"
              />
            </div>

            <!-- Margens -->
            <div class="col-12">
              <label class="form-label fw-medium mb-2">
                <i class="bi bi-arrows-expand me-1"></i> Margens (mm)
              </label>

              <!-- Cards de pré-definição -->
              <div class="d-flex gap-2 mb-3 flex-wrap">
                <button
                  type="button"
                  class="card-margem"
                  :class="{ ativo: margemAtiva === 'muitoEstreita' }"
                  @click="aplicarPreDefinicaoMargens('muitoEstreita')"
                >
                  <span class="fw-medium">Muito Estreita</span>
                  <span class="small text-muted">15 · 15 · 15 · 15</span>
                </button>
                <button
                  type="button"
                  class="card-margem"
                  :class="{ ativo: margemAtiva === 'estreita' }"
                  @click="aplicarPreDefinicaoMargens('estreita')"
                >
                  <span class="fw-medium">Estreita</span>
                  <span class="small text-muted">25 · 25 · 25 · 25</span>
                </button>
                <button
                  type="button"
                  class="card-margem"
                  :class="{ ativo: margemAtiva === 'normal' }"
                  @click="aplicarPreDefinicaoMargens('normal')"
                >
                  <span class="fw-medium">Normal</span>
                  <span class="small text-muted">40 · 60 · 40 · 60</span>
                </button>
                <button
                  type="button"
                  class="card-margem"
                  :class="{ ativo: margemAtiva === 'personalizado' }"
                >
                  <span class="fw-medium">Personalizado</span>
                  <span class="small text-muted">Ajuste livre</span>
                </button>
              </div>

              <!-- Campos de margem -->
              <div class="row g-2">
                <div class="col-3">
                  <CampoInput
                    id="marginTop"
                    label="Topo"
                    type="number"
                    v-model.number="configLayout.margins.top"
                  />
                </div>
                <div class="col-3">
                  <CampoInput
                    id="marginRight"
                    label="Direita"
                    type="number"
                    v-model.number="configLayout.margins.right"
                  />
                </div>
                <div class="col-3">
                  <CampoInput
                    id="marginBottom"
                    label="Fundo"
                    type="number"
                    v-model.number="configLayout.margins.bottom"
                  />
                </div>
                <div class="col-3">
                  <CampoInput
                    id="marginLeft"
                    label="Esquerda"
                    type="number"
                    v-model.number="configLayout.margins.left"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Slots de Imagem -->
      <div class="col-12 mt-4">
        <h6 class="border-bottom pb-2 mb-3">
          <i class="bi bi-image me-1"></i> Slots de Imagem do Template
        </h6>
        <p class="small text-muted mb-3">
          Declare quais imagens este template espera receber. Na empresa, o admin poderá selecionar qual imagem enviada preenche cada slot.
        </p>
        <div class="table-responsive border rounded p-2 bg-body-tertiary">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Chave</th>
                <th>Label</th>
                <th>Tipo de Imagem</th>
                <th style="width: 80px;" class="text-center">Larg. Máx (px)</th>
                <th style="width: 80px;" class="text-center">Alt. Máx (px)</th>
                <th style="width: 80px;" class="text-center">Obrig.</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(slot, idx) in modal.slotsImagem" :key="idx">
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="slot.chave" placeholder="logoPrincipal">
                </td>
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="slot.label" placeholder="Logo Principal">
                </td>
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="slot.tipoImagem" placeholder="LOGO_PRINCIPAL">
                </td>
                <td>
                  <input type="number" class="form-control form-control-sm" v-model.number="slot.maxWidth" placeholder="180" min="1">
                </td>
                <td>
                  <input type="number" class="form-control form-control-sm" v-model.number="slot.maxHeight" placeholder="60" min="1">
                </td>
                <td class="text-center">
                  <input class="form-check-input" type="checkbox" v-model="slot.obrigatorio">
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm text-danger" @click="removerSlotImagem(idx)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="modal.slotsImagem.length === 0">
                <td colspan="7" class="text-center text-muted py-3">
                  Nenhum slot de imagem configurado.
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="btn btn-sm btn-link text-primary p-0 mt-2" @click="adicionarSlotImagem">
            <i class="bi bi-plus-lg me-1"></i> Adicionar Slot de Imagem
          </button>
        </div>
      </div>
    </div>
  </ModalFormularioV2>

</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}

/* ================================================================ */
/* CARDS DE ORIENTAÇÃO                                              */
/* ================================================================ */
.card-orientacao {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  border: 2px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 110px;
}
.card-orientacao:hover {
  border-color: var(--bs-primary);
  background: var(--bs-tertiary-bg);
}
.card-orientacao.ativo {
  border-color: var(--bs-primary);
  background: color-mix(in srgb, var(--bs-primary) 8%, var(--bs-body-bg));
  box-shadow: 0 0 0 1px var(--bs-primary);
}

.icone-pagina {
  display: flex;
  align-items: center;
  justify-content: center;
}
.icone-pagina .pagina-miniatura {
  width: 32px;
  height: 44px;
  border: 2px solid var(--bs-body-color);
  border-radius: 3px;
  background: var(--bs-body-bg);
  transition: all 0.15s ease;
}
.icone-paisagem .pagina-miniatura {
  width: 44px;
  height: 32px;
}
.card-orientacao.ativo .pagina-miniatura {
  border-color: var(--bs-primary);
  background: color-mix(in srgb, var(--bs-primary) 12%, var(--bs-body-bg));
}

/* ================================================================ */
/* CARDS DE PRÉ-DEFINIÇÃO DE MARGEM                                 */
/* ================================================================ */
.card-margem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 16px;
  border: 2px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 100px;
}
.card-margem:hover {
  border-color: var(--bs-primary);
  background: var(--bs-tertiary-bg);
}
.card-margem.ativo {
  border-color: var(--bs-primary);
  background: color-mix(in srgb, var(--bs-primary) 8%, var(--bs-body-bg));
  box-shadow: 0 0 0 1px var(--bs-primary);
}
</style>
